import {
  AfterViewInit,
  Component,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { ConfigProvider } from "../../../config-provider.service";
import { MatError, MatFormField, MatHint } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { NgIf, NgStyle } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { MatInput } from "@angular/material/input";
import { MatTooltip } from "@angular/material/tooltip";
import { MatIconButton } from "@angular/material/button";

export const INPUT_IMAGE_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => InputImageComponent),
  multi: true
};

// doc pour le ngModel : http://almerosteyn.com/2016/04/linkup-custom-control-to-ngcontrol-ngmodel

@Component({
  selector: 'input-image',
  providers: [INPUT_IMAGE_VALUE_ACCESSOR],
  styleUrls: ['./input-image.scss'],
  standalone: true,
  imports: [
    MatError,
    MatHint,
    MatFormField,
    MatError,
    MatIcon,
    NgIf,
    ReactiveFormsModule,
    TranslateModule,
    MatInput,
    MatTooltip,
    MatIconButton,
    NgStyle
  ],
  templateUrl: './input-image.html'
})
export class InputImageComponent implements AfterViewInit, ControlValueAccessor {
  @Input() public set disabled(disabled: boolean) {
    this._disabled = disabled;
    if (this._disabled) {
      this.imageFormControl.disable();
    } else {
      this.imageFormControl.enable();
    }
  }
  public get disabled() {
    return this._disabled;
  }
  public ERROR_EXTENSION = 1;
  public ERROR_SIZE = 2;
  @ViewChild('fileInput', {static: false}) public fileInput : any;
  @ViewChild('textInput', {static: false}) public textInput : any;

  @Input() public previewImage: any;
  public imageConf: ImageData = { current: null, new: null };
  public errorCode = 0;
  public textInputAutoRemove = '';
  public isDragging = false;

  @Input() public placeholder: string = '';
  @Input() public hint: string | null = null;
  @Input() public imageHost: string | null = null;
  @Input() public allowedExtensions: string[] = ['jpg', 'jpeg', 'png'];
  @Input() public maxFileSize = 0; // in Mo, 0 or less = unlimited
  @Output() public imageChange: EventEmitter<any> = new EventEmitter();
  @Output() public onFileDelete: EventEmitter<void> = new EventEmitter<void>();
  @Output('change') public change: EventEmitter<any> = new EventEmitter();

  public imageFormControl: FormControl = new FormControl('', [
    // doc : https://blog.thoughtram.io/angular/2016/03/14/custom-validators-in-angular-2.html#building-a-custom-validator
    (control: any) => this.errorCode !== this.ERROR_EXTENSION ? null : { extension: { valid: false } },
    (control: any) => this.errorCode === this.ERROR_SIZE ? { size: { valid: false } } : null
  ]);
  private _disabled = false;
  constructor(
    public conf: ConfigProvider
  ) { }

  public ngAfterViewInit() {

  }

  public writeValue(value: any): void {
    if (value) {
      this.imageConf.current = value.current;
      this.imageConf.new = value.new;
    } else {
      this.imageConf.current = null;
      this.imageConf.new = null;
    }
  }

  public onImageDragOver(event: any): void {
    event.preventDefault();
    this.isDragging = true;
  }

  public onImageDragLeave(event: any): void {
    event.preventDefault();
    this.isDragging = false;
  }

  public onImageDrop(event: any): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    if (this.disabled) {
      return;
    }

    const files: FileList = event.dataTransfer.files;
    if (files.length) {
      this.selectFile(files[0]);
    }
  }

  @HostListener('imageChange')
  public onChange = (_: any) => { };
  public registerOnChange(fn: any) {
    this.onChange = fn;
  }

  public onTouched = () => { };
  public registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  public onBlur(): void {
    this.onTouched();
  }

  public onFocus(): void {
    if (this.disabled) {
      return;
    }

    this.openFileBrowser();
  }

  public onTextChange(): void {
    if (this.disabled) {
      return;
    }

    this.openFileBrowser();
    this.textInputAutoRemove = '';
  }

  public onRemoveImageClicked(): void {
    if (this.disabled) {
      return;
    }

    this.removeImage();
  }

  public onFileSelectionChange(event:any): void {
    if (this.disabled) {
      return;
    }

    const files: FileList = event.target.files;
    if (files.length) {
      this.selectFile(files[0]);
    }
  }

  public getSelectedImage(): File {
    return this.imageConf.new;
  }

  public openFileBrowser(): void {
    this.fileInput.nativeElement.click();
  }

  private selectFile(file: File): void {
    const fileExtension: string = this.getFileExtension(file);
    if (this.allowedExtensions.length > 0 && this.allowedExtensions.indexOf(fileExtension.toLowerCase()) < 0) {
      this.onError(this.ERROR_EXTENSION, fileExtension);
      return;
    }

    const fileSize: number = file.size / 1048576; // converson o => Mo
    if (this.maxFileSize && fileSize > this.maxFileSize) {
      this.onError(this.ERROR_SIZE, fileSize);
      return;
    }

    this.errorCode = 0;
    this.imageConf.new = file;
    const reader: FileReader = new FileReader();
    reader.onload = () => {
      this.previewImage = reader.result;
    };
    reader.readAsDataURL(file);
    this.onChange(this.imageConf);
    this.imageFormControl.updateValueAndValidity();
  }

  private removeImage(): void {
    this.fileInput.nativeElement.value = null;
    this.imageConf.new = null;
    this.imageConf.current = null;
    this.previewImage = null;
    this.onChange(this.imageConf);
    this.change.emit(this.imageConf);
    this.onFileDelete.emit();
  }

  private getFileExtension(file: File): string {
    const parts: string[] = file.name.split('.');

    return parts[parts.length - 1];
  }

  private onError(errorCode: number, extra: any): void {
    this.errorCode = errorCode;
    this.textInput.nativeElement.blur();
    this.imageFormControl.updateValueAndValidity();
  }
}

export interface ImageData {
  current: string|null;
  // on peut pas explicitement mettre un type File parce que
  // sinon on peut pas attribuer cette valeur � un model
  // dont le champs file est d�clar� en type string
  // new: File;
  new: any;
}
